import React, { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../config";
import {
  SCORE_OPTIONS,
  ALL_FIELDS,
  VOICE_FIELDS,
  STORY_FIELDS,
  CRITERIA_LABELS,
  totalScore,
} from "../utils/scoreComputation";

const JudgeScoring = () => {
  // Auth state
  const [judges, setJudges] = useState([]);
  const [selectedJudgeId, setSelectedJudgeId] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Data state
  const [season, setSeason] = useState(null);
  const [noSeason, setNoSeason] = useState(false);
  const [performances, setPerformances] = useState([]);
  const [scores, setScores] = useState({}); // { [performance_id]: { shruti: ..., ... } }
  const [isLoading, setIsLoading] = useState(false);

  // Category tab state
  const [activeCategory, setActiveCategory] = useState("");

  // Video player state
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [activeStartSec, setActiveStartSec] = useState(0);
  const [activePerfId, setActivePerfId] = useState(null);

  // Save feedback state: { [performance_id]: 'saving' | 'saved' | 'error' }
  const [saveStatus, setSaveStatus] = useState({});

  // Scoring guide modal
  const [showGuide, setShowGuide] = useState(false);

  // YouTube IFrame API for seek controls
  const playerRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);

  // ─── YouTube IFrame API ───
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
  }, []);

  // Create/update player when video changes
  useEffect(() => {
    if (!activeVideoId) {
      // Cleanup when video closed
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (e) {}
        playerRef.current = null;
      }
      return;
    }

    const createPlayer = () => {
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (e) {}
        playerRef.current = null;
      }
      playerRef.current = new window.YT.Player("judge-yt-player", {
        videoId: activeVideoId,
        playerVars: { autoplay: 1, start: activeStartSec || 0, rel: 0, modestbranding: 1 },
        events: {
          onReady: () => {
            if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
            timeIntervalRef.current = setInterval(() => {
              if (playerRef.current?.getCurrentTime) {
                setCurrentTime(Math.floor(playerRef.current.getCurrentTime()));
              }
            }, 500);
          },
        },
      });
    };

    // Small delay to let the div render
    const timer = setTimeout(() => {
      if (window.YT && window.YT.Player) {
        createPlayer();
      } else {
        window.onYouTubeIframeAPIReady = createPlayer;
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    };
  }, [activeVideoId, activeStartSec]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (e) {}
      }
    };
  }, []);

  const seekBy = useCallback((deltaSec) => {
    if (playerRef.current?.getCurrentTime && playerRef.current?.seekTo) {
      const cur = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.max(0, cur + deltaSec), true);
    }
  }, []);

  const fmtSec = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  // ---------- Step 1: Fetch judges on mount ----------
  useEffect(() => {
    const fetchJudges = async () => {
      const { data, error } = await supabase
        .from("storytellers")
        .select("storyteller_id, storyteller_telugu, storyteller_english, photo_url, judge_sequence")
        .eq("is_judge", true)
        .order("judge_sequence");
      if (error) {
        console.error("Error fetching judges:", error);
        return;
      }
      setJudges(data || []);
    };
    fetchJudges();
  }, []);

  // ---------- Step 2: Password check ----------
  const handleLogin = () => {
    const judge = judges.find((j) => j.storyteller_id === selectedJudgeId);
    if (!judge) return;
    const firstName = (judge.storyteller_english || "").split(" ")[0].toLowerCase();
    // Per-judge password overrides
    const passwordOverrides = { "narayanaswamy": "nasy" };
    const base = passwordOverrides[firstName] || firstName;
    const expectedPassword = base + (judge.judge_sequence || "");
    if (password.toLowerCase() === expectedPassword) {
      setPasswordError("");
      setIsAuthenticated(true);
    } else {
      setPasswordError("Incorrect password. Try again.");
    }
  };

  // ---------- Step 3: Load scoring data after auth ----------
  const loadScoringData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch active judging season
      const { data: seasonData, error: seasonError } = await supabase
        .from("jeedigunta_seasons")
        .select("*")
        .eq("is_active", true)
        .limit(1)
        .single();

      if (seasonError || !seasonData) {
        setNoSeason(true);
        setIsLoading(false);
        return;
      }
      setSeason(seasonData);
      const seasonId = seasonData.season_id;

      // Fetch shortlisted performances
      const { data: shortlist, error: shortlistErr } = await supabase
        .from("jeedigunta_shortlist")
        .select("*")
        .eq("season_id", seasonId);

      if (shortlistErr || !shortlist || shortlist.length === 0) {
        setPerformances([]);
        setIsLoading(false);
        return;
      }

      const perfIds = shortlist.map((s) => s.performance_id);

      // Fetch performances
      const { data: perfs } = await supabase
        .from("performances")
        .select("*")
        .in("performance_id", perfIds);

      const storyIds = [...new Set((perfs || []).map((p) => p.story_id).filter(Boolean))];
      const episodeIds = [...new Set((perfs || []).map((p) => p.episode_id).filter(Boolean))];

      // Fetch stories with writers, and episodes
      const [storiesRes, episodesRes] = await Promise.all([
        storyIds.length > 0
          ? supabase.from("stories").select("*, writers(*)").in("story_id", storyIds)
          : { data: [] },
        episodeIds.length > 0
          ? supabase.from("episodes").select("*").in("episode_id", episodeIds)
          : { data: [] },
      ]);

      const storiesMap = {};
      (storiesRes.data || []).forEach((s) => {
        storiesMap[s.story_id] = s;
      });

      const episodesMap = {};
      (episodesRes.data || []).forEach((e) => {
        episodesMap[e.episode_id] = e;
      });

      // Merge into enriched performance list
      const enriched = (perfs || []).map((p) => ({
        ...p,
        story: storiesMap[p.story_id] || null,
        episode: episodesMap[p.episode_id] || null,
        shortlist: shortlist.find((s) => s.performance_id === p.performance_id),
      }));

      setPerformances(enriched);

      // Fetch existing judge scores
      const { data: existingScores } = await supabase
        .from("jeedigunta_judge_scores")
        .select("*")
        .eq("season_id", seasonId)
        .eq("judge_storyteller_id", selectedJudgeId);

      const scoresMap = {};
      (existingScores || []).forEach((row) => {
        const obj = {};
        ALL_FIELDS.forEach((f) => {
          obj[f] = row[f] !== null && row[f] !== undefined ? Number(row[f]).toFixed(1) : "";
        });
        scoresMap[row.performance_id] = obj;
      });
      setScores(scoresMap);
    } catch (err) {
      console.error("Error loading scoring data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedJudgeId]);

  useEffect(() => {
    if (isAuthenticated && selectedJudgeId) {
      loadScoringData();
    }
  }, [isAuthenticated, selectedJudgeId, loadScoringData]);

  // ---------- Read-only check ----------
  // When a season is closed (jeedigunta_seasons.status='closed'),
  // judges and admins can still view scores but cannot edit. This is
  // enforced both client-side (we short-circuit the upsert and disable
  // inputs) and server-side via RLS policies in db/season-close-rls.sql.
  const isReadOnly = season?.status === "closed";

  // ---------- Score change handler with auto-save ----------
  const handleScoreChange = async (performanceId, field, value) => {
    if (isReadOnly) return;
    // Update local state immediately
    setScores((prev) => {
      const current = prev[performanceId] || {};
      return {
        ...prev,
        [performanceId]: { ...current, [field]: value },
      };
    });

    setSaveStatus((prev) => ({ ...prev, [performanceId]: "saving" }));

    // Build the full score object from updated local state
    const currentScores = scores[performanceId] || {};
    const updatedScores = { ...currentScores, [field]: value };

    const upsertRow = {
      season_id: season.season_id,
      performance_id: performanceId,
      judge_storyteller_id: selectedJudgeId,
      updated_at: new Date().toISOString(),
    };

    ALL_FIELDS.forEach((f) => {
      const val = updatedScores[f];
      upsertRow[f] = val !== "" && val !== undefined ? parseFloat(val) : null;
    });

    const { error } = await supabase
      .from("jeedigunta_judge_scores")
      .upsert(upsertRow, { onConflict: "season_id,performance_id,judge_storyteller_id" });

    if (error) {
      console.error("Error saving score:", error);
      setSaveStatus((prev) => ({ ...prev, [performanceId]: "error" }));
    } else {
      setSaveStatus((prev) => ({ ...prev, [performanceId]: "saved" }));
      setTimeout(() => {
        setSaveStatus((prev) => {
          if (prev[performanceId] === "saved") {
            const next = { ...prev };
            delete next[performanceId];
            return next;
          }
          return prev;
        });
      }, 1500);
    }
  };

  // ---------- Helper: compute total for a performance ----------
  const getTotal = (performanceId) => {
    const row = scores[performanceId];
    if (!row) return null;
    return totalScore(row);
  };

  // ---------- Helper: extract youtube video id ----------
  const extractVideoId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^?&/]+)/
    );
    return match ? match[1] : null;
  };

  // ---------- Render: Pre-auth (judge selection) ----------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-600 text-white py-8 px-4 text-center">
          <h1 className="text-3xl font-bold">జీడిగుంట కథాకళ పురస్కారాలు</h1>
          <p className="mt-2 text-green-100 text-lg">Judge Scoring Portal</p>
        </div>

        <div className="max-w-md mx-auto mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
            Who is Judging?
          </h2>

          {/* Judge dropdown */}
          <select
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg mb-4 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={selectedJudgeId}
            onChange={(e) => {
              setSelectedJudgeId(e.target.value);
              setPassword("");
              setPasswordError("");
            }}
          >
            <option value="">-- Select Judge --</option>
            {judges.map((j) => (
              <option key={j.storyteller_id} value={j.storyteller_id}>
                {j.storyteller_telugu} ({j.storyteller_english})
              </option>
            ))}
          </select>

          {/* Password input */}
          {selectedJudgeId && (
            <>
              <input
                type="password"
                placeholder="Enter password"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg mb-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              />
              {passwordError && (
                <p className="text-red-500 text-sm mb-2">{passwordError}</p>
              )}
              <button
                onClick={handleLogin}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition"
              >
                Login
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---------- Render: Loading ----------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading scoring data...</p>
        </div>
      </div>
    );
  }

  // ---------- Render: No active season ----------
  if (noSeason) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        <div className="bg-gradient-to-r from-green-700 to-green-600 text-white py-8 px-4 text-center">
          <h1 className="text-3xl font-bold">జీడిగుంట కథాకళ పురస్కారాలు</h1>
        </div>
        <div className="max-w-md mx-auto mt-12 bg-white rounded-xl shadow-lg p-8 text-center">
          <p className="text-gray-600 text-lg">No active judging season.</p>
        </div>
      </div>
    );
  }

  // ---------- Render: Scoring grid ----------
  const currentJudge = judges.find((j) => j.storyteller_id === selectedJudgeId);

  // Extract unique categories from performances
  const categories = [...new Set(performances.map((p) => p.category).filter(Boolean))].sort();
  const CATEGORY_LABELS = {
    "గళకళ": { telugu: "గళకళ", english: "Voice Art", color: "amber" },
    "కలం-గళం": { telugu: "కలం-గళం", english: "Writer-Voice", color: "purple" },
    "తొలి కథనం - గళం": { telugu: "తొలి కథనం - గళం", english: "Debut Voice", color: "teal" },
    "తొలి కథనం - కలం": { telugu: "తొలి కథనం - కలం", english: "Debut Author", color: "rose" },
  };

  // Set default category on first load
  if (!activeCategory && categories.length > 0) {
    setActiveCategory(categories[0]);
  }

  // Filter performances by active category, sorted by storyteller Telugu name
  const filteredPerformances = performances
    .filter((p) => p.category === activeCategory)
    .sort((a, b) => (a.storyteller_name_telugu || "").localeCompare(b.storyteller_name_telugu || "", "te"));

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-600 text-white py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">జీడిగుంట కథాకళ పురస్కారాలు</h1>
            {season && (
              <p className="text-green-100 text-sm mt-1">
                {season.season_name || season.season_id}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowGuide(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2"
            >
              <span>📋</span> Scoring Guide
            </button>
            {currentJudge?.photo_url && (
              <img
                src={currentJudge.photo_url}
                alt=""
                className="w-10 h-10 rounded-full object-cover border-2 border-white"
              />
            )}
            <span className="text-green-100">
              {currentJudge?.storyteller_telugu}
            </span>
          </div>
        </div>
      </div>

      {/* Closed-season banner — view-only */}
      {isReadOnly && (
        <div className="bg-amber-50 border-b border-amber-300 py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center gap-3 text-sm">
            <span className="bg-amber-200 text-amber-900 font-bold rounded-full px-3 py-1 text-xs">
              🔒 SEASON CLOSED
            </span>
            <span className="text-amber-900">
              This season has been closed by the administrator. Scores and shortlist are
              frozen — view-only. Results, the Judges side-by-side dashboard, and per-judge
              scoring history all remain accessible.
            </span>
          </div>
        </div>
      )}

      {/* Scoring Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowGuide(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Guide Header */}
            <div className="bg-gradient-to-r from-green-700 to-green-600 text-white px-8 py-5 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">📋 Scoring Guide</h2>
                <p className="text-green-100 text-sm mt-1">10 Criteria &middot; Scale: 0 &ndash; 10 (0.5 increments)</p>
              </div>
              <button onClick={() => setShowGuide(false)} className="text-white/80 hover:text-white text-2xl font-bold">&times;</button>
            </div>

            <div className="p-6">
              {/* Two-column criteria */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Voice */}
                <div>
                  <h3 className="text-lg font-bold text-amber-700 mb-3 flex items-center gap-2">
                    <span className="bg-amber-100 w-8 h-8 rounded-lg flex items-center justify-center text-base">🎙</span>
                    గళకళ (Voice &mdash; 4 criteria)
                  </h3>
                  <div className="space-y-2">
                    {[
                      { num: 1, te: "శ్రుతి", en: "Shruti", desc: "స్వర స్థాయి, స్వర నాణ్యత — గొంతు సహజత్వం, స్వరం స్థిరత్వం, శ్రావ్యత" },
                      { num: 2, te: "మాడ్యులేషన్", en: "Modulation", desc: "స్వర వైవిధ్యం, భావోద్వేగ శ్రేణి — పాత్రల గొంతులు, సన్నివేశానుగుణ మార్పులు" },
                      { num: 3, te: "ఉచ్చారణ", en: "Uccharana", desc: "పదాల స్పష్టత — ప్రతి అక్షరం వినిపించాలి, యాస ఆమోదయోగ్యం" },
                      { num: 4, te: "గతి", en: "Gati", desc: "వేగం, లయ, టైమింగ్ — కథకు తగ్గ వేగం, విరామాలు, శ్రోతల్ని నిలిపే నేర్పు" },
                    ].map((c) => (
                      <div key={c.num} className="bg-amber-50 border-l-4 border-amber-400 rounded-lg px-4 py-3 flex items-start gap-3">
                        <span className="bg-amber-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{c.num}</span>
                        <div>
                          <div className="font-bold text-sm">{c.te} <span className="text-gray-400 font-normal text-xs ml-1">{c.en}</span></div>
                          <div className="text-xs text-gray-600 mt-1">{c.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Story */}
                <div>
                  <h3 className="text-lg font-bold text-blue-700 mb-3 flex items-center gap-2">
                    <span className="bg-blue-100 w-8 h-8 rounded-lg flex items-center justify-center text-base">📖</span>
                    కథా నాణ్యత (Story &mdash; 6 criteria)
                  </h3>
                  <div className="space-y-2">
                    {[
                      { num: 5, te: "కథ", en: "Story", desc: "కథ మొత్తం నాణ్యత — కథాంశం, భావోద్వేగం, సందేశం, ప్రభావం" },
                      { num: 6, te: "రచన", en: "Writing Craft", desc: "రచయిత నైపుణ్యం — భాష, వాక్య నిర్మాణం, సంభాషణలు, వర్ణనలు" },
                      { num: 7, te: "రీడబిలిటీ", en: "Readability", desc: "భాష స్పష్టత, ప్రవాహం — సులభంగా వినగలిగే, అర్థమయ్యే రచన" },
                      { num: 8, te: "వస్తు", en: "Subject Depth", desc: "విషయ లోతు, ఇతివృత్త గాఢత — కథ చెప్పదలచుకున్నది ఎంత లోతుగా చెప్పింది" },
                      { num: 9, te: "శిల్పం", en: "Narrative Structure", desc: "కథా నిర్మాణం, సాహిత్య శిల్పం — ఆరంభం, మధ్య, ముగింపు, మలుపులు" },
                      { num: 10, te: "నూతనత్వం", en: "Innovation", desc: "సృజనాత్మకత, తాజాదనం — కొత్త ఆలోచన, భిన్నమైన దృక్కోణం, ప్రయోగం" },
                    ].map((c) => (
                      <div key={c.num} className="bg-blue-50 border-l-4 border-blue-400 rounded-lg px-4 py-3 flex items-start gap-3">
                        <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{c.num}</span>
                        <div>
                          <div className="font-bold text-sm">{c.te} <span className="text-gray-400 font-normal text-xs ml-1">{c.en}</span></div>
                          <div className="text-xs text-gray-600 mt-1">{c.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Formulas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300 rounded-xl p-4 text-center">
                  <div className="font-bold text-amber-800 mb-2">🎙 గళకళ / తొలి కథనం-గళం</div>
                  <div className="text-lg font-extrabold text-amber-900"><span className="text-2xl">80%</span> Voice + <span className="text-2xl">20%</span> Story</div>
                  <div className="text-xs text-amber-700 mt-2">Voice = శ్రుతి + మాడ్యులేషన్ + ఉచ్చారణ + గతి (max 40)</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300 rounded-xl p-4 text-center">
                  <div className="font-bold text-blue-800 mb-2">📖 కలం-గళం / తొలి కథనం-కలం</div>
                  <div className="text-lg font-extrabold text-blue-900"><span className="text-2xl">75%</span> Story + <span className="text-2xl">25%</span> Voice</div>
                  <div className="text-xs text-blue-700 mt-2">Story = కథ + రచన + రీడబిలిటీ + వస్తు + శిల్పం + నూతనత్వం (max 60)</div>
                </div>
                <div className="bg-green-50 border border-green-300 rounded-xl p-4">
                  <div className="font-bold text-green-800 mb-2">📋 గమనించండి</div>
                  <ul className="text-xs text-green-900 space-y-1">
                    <li>&#10003; 10 criteria అన్నీ తప్పనిసరి</li>
                    <li>&#10003; 0.5 increments (0, 0.5, 1.0 ... 10.0)</li>
                    <li>&#10003; Scores auto-save అవుతాయి</li>
                    <li>&#10003; &#9654; బటన్ నొక్కి వీడియో చూడండి</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => {
            const label = CATEGORY_LABELS[cat] || { telugu: cat, english: "", color: "gray" };
            const isActive = activeCategory === cat;
            const count = performances.filter((p) => p.category === cat).length;
            const scored = performances.filter((p) => p.category === cat && scores[p.performance_id] && ALL_FIELDS.every((f) => scores[p.performance_id][f])).length;
            return (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setActiveVideoId(null); setActivePerfId(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-green-600 text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div>{label.telugu}</div>
                <div className="text-xs font-normal opacity-75">{label.english} ({scored}/{count})</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Video Player - above table, sticky */}
      {activeVideoId && (
        <div className="sticky top-0 z-40 bg-green-50 border-b border-green-200 py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-start gap-4">
            <div className="rounded-lg shadow-md overflow-hidden" style={{ width: 480, height: 270 }}>
              <div id="judge-yt-player" style={{ width: "100%", height: "100%" }} />
            </div>
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              {(() => {
                const perf = performances.find(
                  (p) => p.performance_id === activePerfId
                );
                if (!perf) return null;
                const storyTitle = perf.story?.title_telugu || perf.story_title || "";
                const writerName = perf.story?.writers?.name_telugu || perf.story?.writer_name || "";
                const storytellerName = perf.storyteller_name_telugu || "";
                return (
                  <div className="text-sm space-y-1">
                    <div className="font-bold text-gray-800 text-base">{storyTitle}</div>
                    {writerName && (
                      <div className="text-gray-600">
                        <span className="text-gray-400 text-xs">రచన:</span> {writerName}
                      </div>
                    )}
                    <div className="text-gray-600">
                      <span className="text-gray-400 text-xs">కథకులు:</span> {storytellerName}
                    </div>
                  </div>
                );
              })()}
              {/* Seek controls */}
              <div className="flex items-center gap-1 flex-wrap mt-1">
                {[
                  { label: "-1m", sec: -60 },
                  { label: "-30s", sec: -30 },
                  { label: "-15s", sec: -15 },
                  { label: "-10s", sec: -10 },
                ].map((b) => (
                  <button
                    key={b.label}
                    onClick={() => seekBy(b.sec)}
                    className="px-2 py-1 bg-white border border-gray-300 rounded text-gray-600 text-xs font-mono hover:bg-gray-100 hover:border-gray-400 active:scale-95 transition-all"
                  >
                    {b.label}
                  </button>
                ))}
                <div className="px-2 py-1 bg-green-100 border border-green-300 rounded text-green-800 font-mono text-xs min-w-[56px] text-center tabular-nums">
                  {fmtSec(currentTime)}
                </div>
                {[
                  { label: "+10s", sec: 10 },
                  { label: "+15s", sec: 15 },
                  { label: "+30s", sec: 30 },
                  { label: "+1m", sec: 60 },
                ].map((b) => (
                  <button
                    key={b.label}
                    onClick={() => seekBy(b.sec)}
                    className="px-2 py-1 bg-white border border-gray-300 rounded text-gray-600 text-xs font-mono hover:bg-gray-100 hover:border-gray-400 active:scale-95 transition-all"
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setActiveVideoId(null); setActivePerfId(null); }}
                className="text-xs text-red-500 hover:text-red-700 underline self-start"
              >
                Close video
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scoring Table */}
      <div className="max-w-full mx-auto p-4">
        <div className="bg-white rounded-xl shadow-lg overflow-auto max-h-[80vh]">
            <table className="w-full text-sm border-collapse">
              {/* Column group headers */}
              <thead className="sticky top-0 z-30">
                <tr>
                  {/* Sticky info columns - no group header */}
                  <th className="sticky left-0 z-40 bg-white border-b border-r" style={{ minWidth: "40px" }}></th>
                  <th className="sticky z-40 bg-white border-b border-r" style={{ left: "40px", minWidth: "180px" }}></th>
                  <th className="sticky z-40 bg-white border-b border-r" style={{ left: "220px", minWidth: "120px" }}></th>
                  <th className="sticky z-40 bg-white border-b border-r" style={{ left: "340px", minWidth: "120px" }}></th>
                  <th className="bg-white border-b border-r" style={{ minWidth: "40px" }}></th>
                  {/* Voice group */}
                  <th
                    colSpan={VOICE_FIELDS.length}
                    className="bg-amber-100 text-amber-800 border-b border-r px-2 py-2 text-center font-semibold"
                  >
                    గళకళ (Voice)
                  </th>
                  {/* Story group */}
                  <th
                    colSpan={STORY_FIELDS.length}
                    className="bg-blue-100 text-blue-800 border-b border-r px-2 py-2 text-center font-semibold"
                  >
                    కథా నాణ్యత (Story)
                  </th>
                  {/* Total */}
                  <th className="bg-gray-100 border-b px-2 py-2"></th>
                  {/* Save status */}
                  <th className="bg-white border-b" style={{ minWidth: "30px" }}></th>
                </tr>

                {/* Individual column headers */}
                <tr className="text-xs">
                  <th className="sticky left-0 z-40 bg-gray-50 border-b border-r px-2 py-2 text-center">#</th>
                  <th className="sticky z-40 bg-gray-50 border-b border-r px-2 py-2 text-left" style={{ left: "40px" }}>
                    కథ పేరు
                  </th>
                  <th className="sticky z-40 bg-gray-50 border-b border-r px-2 py-2 text-left" style={{ left: "220px" }}>
                    రచయిత
                  </th>
                  <th className="sticky z-40 bg-gray-50 border-b border-r px-2 py-2 text-left" style={{ left: "340px" }}>
                    కథకులు
                  </th>
                  <th className="bg-gray-50 border-b border-r px-2 py-2 text-center">
                    ▶
                  </th>
                  {VOICE_FIELDS.map((f) => (
                    <th
                      key={f}
                      className="bg-amber-50 border-b border-r px-2 py-2 text-center whitespace-nowrap"
                    >
                      <div>{CRITERIA_LABELS[f].telugu}</div>
                      <div className="text-gray-400 font-normal">{CRITERIA_LABELS[f].english}</div>
                    </th>
                  ))}
                  {STORY_FIELDS.map((f) => (
                    <th
                      key={f}
                      className="bg-blue-50 border-b border-r px-2 py-2 text-center whitespace-nowrap"
                    >
                      <div>{CRITERIA_LABELS[f].telugu}</div>
                      <div className="text-gray-400 font-normal">{CRITERIA_LABELS[f].english}</div>
                    </th>
                  ))}
                  <th className="bg-gray-100 border-b border-r px-2 py-2 text-center font-bold">
                    Total
                  </th>
                  <th className="bg-gray-50 border-b px-1 py-2"></th>
                </tr>
              </thead>

              <tbody>
                {filteredPerformances.map((perf, idx) => {
                  const perfId = perf.performance_id;
                  const story = perf.story;
                  const episode = perf.episode;
                  const writer = story?.writers;
                  const perfScores = scores[perfId] || {};
                  const total = getTotal(perfId);
                  const videoId = episode?.video_id || extractVideoId(episode?.youtube_url || perf.youtube_url);
                  const isActiveRow = activePerfId === perfId;

                  // storyteller name: from performance
                  const storytellerName =
                    perf.storyteller_name_telugu ||
                    perf.storyteller_telugu ||
                    "";

                  const storyTitle =
                    story?.title_telugu ||
                    perf.story_title ||
                    "";

                  const writerName =
                    writer?.name_telugu ||
                    story?.writer_name ||
                    "";

                  return (
                    <tr
                      key={perfId}
                      className={`border-b hover:bg-gray-50 ${
                        isActiveRow ? "bg-yellow-50" : ""
                      }`}
                    >
                      {/* # */}
                      <td className="sticky left-0 z-10 bg-white border-r px-2 py-2 text-center text-gray-500">
                        {idx + 1}
                      </td>
                      {/* Story name */}
                      <td
                        className="sticky z-10 bg-white border-r px-2 py-2 font-medium text-gray-800"
                        style={{ left: "40px", maxWidth: "180px" }}
                      >
                        <div className="truncate" title={storyTitle}>
                          {storyTitle}
                        </div>
                      </td>
                      {/* Writer */}
                      <td
                        className="sticky z-10 bg-white border-r px-2 py-2 text-gray-600"
                        style={{ left: "220px", maxWidth: "120px" }}
                      >
                        <div className="truncate" title={writerName}>
                          {writerName}
                        </div>
                      </td>
                      {/* Storyteller */}
                      <td
                        className="sticky z-10 bg-white border-r px-2 py-2 text-gray-600"
                        style={{ left: "340px", maxWidth: "120px" }}
                      >
                        <div className="truncate" title={storytellerName}>
                          {storytellerName}
                        </div>
                      </td>
                      {/* Play button */}
                      <td className="border-r px-2 py-2 text-center">
                        {videoId ? (
                          <button
                            onClick={() => {
                              setActiveVideoId(videoId);
                              setActiveStartSec(
                                perf.story_start_sec || 0
                              );
                              setActivePerfId(perfId);
                            }}
                            className="text-green-600 hover:text-green-800 text-lg"
                            title="Play"
                          >
                            ▶
                          </button>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      {/* Voice fields */}
                      {VOICE_FIELDS.map((f) => (
                        <td key={f} className="border-r px-1 py-1 text-center">
                          <select
                            value={perfScores[f] ?? ""}
                            onChange={(e) =>
                              handleScoreChange(perfId, f, e.target.value)
                            }
                            disabled={isReadOnly}
                            className={`w-16 text-center text-sm border border-gray-200 rounded px-1 py-1 focus:ring-1 focus:ring-amber-400 focus:border-amber-400 ${isReadOnly ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""}`}
                          >
                            <option value="">-</option>
                            {SCORE_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </td>
                      ))}
                      {/* Story fields */}
                      {STORY_FIELDS.map((f) => (
                        <td key={f} className="border-r px-1 py-1 text-center">
                          <select
                            value={perfScores[f] ?? ""}
                            onChange={(e) =>
                              handleScoreChange(perfId, f, e.target.value)
                            }
                            disabled={isReadOnly}
                            className={`w-16 text-center text-sm border border-gray-200 rounded px-1 py-1 focus:ring-1 focus:ring-blue-400 focus:border-blue-400 ${isReadOnly ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""}`}
                          >
                            <option value="">-</option>
                            {SCORE_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </td>
                      ))}
                      {/* Total */}
                      <td className="border-r px-2 py-2 text-center font-bold text-gray-800 bg-gray-50">
                        {total !== null ? total.toFixed(1) : "--"}
                      </td>
                      {/* Save status indicator */}
                      <td className="px-1 py-2 text-center">
                        {saveStatus[perfId] === "saving" && (
                          <span className="text-gray-400 text-xs animate-pulse">...</span>
                        )}
                        {saveStatus[perfId] === "saved" && (
                          <span className="text-green-500 text-sm">&#10003;</span>
                        )}
                        {saveStatus[perfId] === "error" && (
                          <span className="text-red-500 text-xs" title="Save failed">!</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {filteredPerformances.length === 0 && (
                  <tr>
                    <td
                      colSpan={5 + ALL_FIELDS.length + 2}
                      className="text-center py-8 text-gray-400"
                    >
                      No shortlisted performances found for this season.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </div>

      </div>
    </div>
  );
};

export default JudgeScoring;
